/* eslint-disable no-param-reassign */
import { parseTransaction } from "./parseTransaction";
import { copyCustomFields } from "./copyCustomFields";
import { select } from "redux-saga/effects";
import { SubscriptionCheck } from "../../../../components/Access/helper/subscription/SubscriptionCheck";
import { SubscriptionScopeKey } from "../../../../components/Access/helper/subscription/enum/SubscriptionScopeKey";
import { getDisplayName } from "../../../../components/Input/ContactPersonInput";
import { getTransactionInitStatus } from "../../../../containers/AppPage/Shoebox/Form/common/getTransactionInitStatus";
import { formatNumber } from "../../../../components/FormatNumber";
import { store } from "../../../store";
import { L } from "../../../List/reducer";
import { readLists } from "./readLists";
import {readOutstanding} from "./readOutstanding";
import { readPendingInvoices } from "./readPendingInvoices";
import TransactionActions from "bukku/redux/Transaction/actions";
import { getNewLinkItemsWithApplyAmount } from "./getNewLinkItemsWithApplyAmount";
import { getInapplicableLinkItems } from "./getInapplicableLinkItems";
import ListHelper from "./ListHelper";
import { put } from "../../../../shared/SagaService";
import { Type } from "../../helper";
import { processItems } from "./processItems";
import { showAutosaveConfirmationModalPromise } from "./Modal/showAutosaveConfirmationModalPromise";
import { writeAutosaveStorage } from "../../../LocalStorage/helpers/AutosaveTransactions/writeStorage";
import dayjs from "dayjs";
import _ from "underscore";
import { parseCustomFields } from "./ListHelper/parseCustomFields";

const {
    parseContactTypes,
    parseContactPersons,
    parseTransactionTableItems,
    // parsePaymentMethods,
    parseTags,
    parseTerms,
    parseAccounts,
    parseNumberFormats,
} = ListHelper;

const {
    changeLinkItems,
} = TransactionActions;

export function* processRestoreAutosaveForm({
    transaction,
    transactionType,
    transactionInitCase,
}) {
    const productList = yield select(state => state.List.productList);
    const contacts = yield select(state => state.List.contacts);
    const settings = yield select(state => state.List.settings);
    const companyCurrencyCode = yield select(state => state.Auth.companyCurrencyCode);

    const invalidLinkedItemArr = [];
    const tooMuchAppliedArr = [];
    const updatedLinkedItemAppliedArr = []; // used for linked items (invoices, bills, Sales/purchase CN, deemed payment)

    // For Subscription items
    let hasNonSubscribedFeatures = false;

    // For autosave List API items (eg contacts, tags etc.)
    let hasListItemsChange = false;
    let invalidContacts = [],
        invalidAccounts = [],
        invalidCPs = [],
        invalidTags = [],
        invalidTerms = [],
        invalidNumberFormats = [],
        invalidCustomFields = [],
        // invalidItemsObj = {},
        invalidFormItemsObj = {},
        invalidTermItemsObj = {},
        invalidDepositItemsObj = {},
        invalidStockItemsObj = {},
        invalidJournalItemsObj = {},
        invalidBankItemsObj = {};

    transaction = yield parseTransaction(transaction, transactionType) || {};

    // ----- Autosave I: Restore only relevant Custom Fields
    const fields = yield copyCustomFields(transactionType, transactionType, transaction);

    // ----- Autosave II: Check modifiers and subscription things 1
    const hasTagSub = SubscriptionCheck.withKeys(SubscriptionScopeKey.TAG).hasSubscriptionTo();
    const hasMultiCurrencySub =
        SubscriptionCheck.withKeys(SubscriptionScopeKey.MULTI_CURRENCY).hasSubscriptionTo();
    const hasCustomFieldSub =
        SubscriptionCheck.withKeys(SubscriptionScopeKey.CUSTOM_FIELD).hasSubscriptionTo();

    const hasTagChange = !hasTagSub && (transaction.tag_ids || []).length !== 0;
    const hasCurrencyCodeChange = !hasMultiCurrencySub && transaction.currency_code &&
    transaction.currency_code !== companyCurrencyCode;
    const hasFieldChange = !hasCustomFieldSub && !!(fields || []).find(f => f.value);

    // Modifiers (Permissions)
    let toApplyStatus;
    const highestStatusAccess = getTransactionInitStatus(transactionType);
    if (highestStatusAccess === 'ready') {
        toApplyStatus = transaction.status || 'ready';
    }
    else if (highestStatusAccess === 'pending_approval') {
        if (transaction.status === 'ready')
            toApplyStatus = 'pending_approval';
        else
            toApplyStatus = transaction.status || 'pending_approval';
    }
    else {
        toApplyStatus = 'draft';
    }

    transaction = {
        ...transaction,
        type: transactionType,

        number: transaction.number,
        date: transaction.date,
        status: toApplyStatus,

        due_date: transaction.due_date, // For bill
        currency_code: hasMultiCurrencySub ? transaction.currency_code : companyCurrencyCode,
        exchange_rate: hasMultiCurrencySub ? transaction.exchange_rate : 1,

        term_items: transaction.term_items || [],
        link_items: transaction.link_items || [],
        sales_link_items: transaction.sales_link_items || [],
        purchase_link_items: transaction.purchase_link_items || [],
        fields: hasCustomFieldSub ? transaction.fields || [] : [],

        linked_items: transaction.linked_items || [],

        // Copy from originTransaction
        // number2: originTransaction.number,
        amount: formatNumber(transaction.amount, 2, undefined, false),
        account2_id: transaction.account2_id,

        tag_ids: hasTagSub ? transaction.tag_ids || [] : [],
    };

    // ----- Autosave III: Check if contact IDs are valid (since need them to readOutstanding and contact persons)
    // If invalid, set as null and add into invalidContacts.
    if (transaction.contact_id && !contacts.find(contact => contact.id === transaction.contact_id)) {
        invalidContacts.push(transaction.autosave_text_values.contact_id);
        transaction.contact_id = null;
    }
    if (transaction.contact2_id && !contacts.find(contact => contact.id === transaction.contact2_id)) {
        invalidContacts.push(transaction.autosave_text_values.contact2_id);
        transaction.contact2_id = null;
    }

    // ----- Autosave IV: Populate contact persons name
    if (transaction.contact_id) {
        yield readLists(
            [L.contact_persons], {
                [L.contact_persons]: { contact_id: transaction.contact_id },
            },
        );
        const { contactPersons } = yield store.getState()?.List || [];

        // if stored billing/shipping cp id without name, get and set the name here,
        // so that form restoration can be done smoothly in updateForm.js
        if (transaction.billing_contact_person_id && !transaction.billing_contact_person) {
            const billingCp = contactPersons.find(cp => cp.id === transaction.billing_contact_person_id);
            if (billingCp)
                transaction.billing_contact_person = getDisplayName(billingCp);
        }

        if (transaction.shipping_contact_person_id && !transaction.shipping_contact_person) {
            const shippingCp = contactPersons.find(
                cp => cp.id === transaction.shipping_contact_person_id);
            if (shippingCp)
                transaction.shipping_contact_person = getDisplayName(shippingCp);
        }
    }

    // -----  Autosave V: Check if all List items are applicable. If not, remove them.
    // 0. Contacts
    // Already handled in step IV

    // 1. Contact types
    const parseContactTypesRes = yield parseContactTypes(transactionType, transaction);
    invalidContacts = [...invalidContacts, ...parseContactTypesRes];

    // 2. Contact Persons
    const { contactPersons } = yield store.getState()?.List || [];
    invalidCPs = yield parseContactPersons(transaction, contactPersons);

    // 3. Accounts
    invalidAccounts = yield parseAccounts(transaction);

    // 4. Tags
    invalidTags = yield parseTags(transaction);

    // 5. Terms
    // *term_id only. term_items (if any) would be done in parseTransactionTableItems
    invalidTerms = yield parseTerms(transaction);

    // 6. Number formats
    invalidNumberFormats = yield parseNumberFormats(transaction, transactionType);

    // 7. Custom fields
    invalidCustomFields = yield parseCustomFields(transaction);

    // 8. Transaction table items: form_items, items, bank_items, journal_items, deposit_items, term_items
    invalidFormItemsObj = yield parseTransactionTableItems(transaction, 'form_items');
    invalidTermItemsObj = yield parseTransactionTableItems(transaction, 'term_items');
    invalidDepositItemsObj = yield parseTransactionTableItems(transaction, 'deposit_items');
    invalidStockItemsObj = yield parseTransactionTableItems(transaction, 'items');
    invalidBankItemsObj = yield parseTransactionTableItems(transaction, 'bank_items');
    invalidJournalItemsObj = yield parseTransactionTableItems(transaction, 'journal_items');

    // 9. Finally Done checking. Make every entry unique, and declare hasListItemsChange.
    // Non-table list items (except custom fields, since each field is always unique)
    hasListItemsChange = false;
    [invalidAccounts, invalidContacts, invalidCPs,
        invalidTags, invalidTerms, invalidNumberFormats].forEach((arr, idx, oriArr) => {
        const uniqueArr = _.uniq(arr);

        if (uniqueArr.length !== 0) {
            hasListItemsChange = true;
            oriArr[idx] = uniqueArr;
        }
    });

    if (invalidCustomFields.length !== 0)
        hasListItemsChange = true;

    // Table list items
    [invalidFormItemsObj, invalidTermItemsObj, invalidDepositItemsObj,
        invalidBankItemsObj, invalidJournalItemsObj, invalidStockItemsObj].forEach((obj, idx, oriArr) => {
        const toSetUniqueObj = {...obj};
        Object.keys(toSetUniqueObj).forEach(key => {
            // toSetUniqueObj[key] = Array.from(new Set(toSetUniqueObj[key]));
            toSetUniqueObj[key] = _.uniq(toSetUniqueObj[key]);

            if (toSetUniqueObj[key].length !== 0)
                hasListItemsChange = true;
        });

        oriArr[idx] = toSetUniqueObj;
    });

    // ----- Autosave VI: Check subscription things 2 (form_items/bank_items/items)
    // We put this here after step V of checking/parsing list items, because this step may make use of some
    // required list items data like product_id. Eg if product_id is null, we can't read products to check the UOMs.
    // That's why we have to parse the invalid items first before this step.
    const {
        finalFormItems, // array

        hasTaxChange, // boolean
        hasLocationChange, // boolean

        nonBaseUomItems, // array
        removedBundles, // array

        hasItemsChange, // boolean
    } = processItems({ transaction, productList, settings, isRestoreAutosaveForm: true});

    hasNonSubscribedFeatures = hasItemsChange || hasTagChange || hasCurrencyCodeChange || hasFieldChange;

    if (transaction.form_items)
        transaction.form_items = finalFormItems;
    else if (transaction.items)
        transaction.items = finalFormItems;
    else if (transaction.bank_items)
        transaction.bank_items = finalFormItems;

    // -----  Autosave VII: Handle link items (read outstanding and see which items are inapplicable).
    // Overall Process:
    // 1. Check transactionType and read outstanding / pending transactions to get 'linkItems'.
    //      - If no relevant data to read, eg no contact_id && currency_code, won't trigger the read.
    //        linkItems will then be empty array.
    // 2. Add in local storage data's link_items' apply_amount into linkItems.
    //      - If some link_item in storage data are no longer applicable (eg those txn are voided, or
    //        linkItems balance is lesser than stored apply_amount), add the link_item number
    //        into 'tooMuchAppliedArr' / 'invalidLinkedItemArr'.
    //      - Then, that link_item will not have any apply_amount.
    //      - 'updatedLinkedItemAppliedArr' is used when there's a change of balance/apply_amount between stored and fetch data.
    // 3. Update transaction to use the newLinkItems.
    // 4. Update redux to use the newLinkItems.
    //      - This will trigger componentDidUpdate in relevant components, eg !_.isEqual(prevProps.linkItems, linkItems),
    //        which will do the updating of UI (set apply_amount and select the linkItems checkbox).

    let toReadOutstanding, toReadPendingInvoices;
    switch (transactionType) {
        case Type.sale_payment:
        case Type.sale_credit_note:
        case Type.sale_refund:
        case Type.purchase_credit_note:
        case Type.purchase_payment:
        case Type.purchase_refund:
            toReadOutstanding = true;
            break;
        case Type.tax_deemed_payment:
            toReadPendingInvoices = true;
            break;
        default:
            break;
    }

    if (toReadOutstanding || toReadPendingInvoices) {
        if (toReadPendingInvoices)
            yield readPendingInvoices(transaction);
        else
            yield readOutstanding(transactionInitCase, transactionType, transaction);

        const linkItems = yield select(state => state.Transaction.linkItems);

        // This step 'getNewLinkItemsWithApplyAmount' also adds inapplicable transaction numbers into tooMuchAppliedArr.
        // - For sales/purchase CN, it also makes sure link items' apply_amount don't exceed
        //   form items total amount (which will involve updatedLinkedItemAppliedArr).
        // - For deemed payment, it's to check if link items' apply_amount is different with stored apply_amount.
        let newLinkItems;
        if ([Type.sale_credit_note, Type.purchase_credit_note, Type.tax_deemed_payment]
            .includes(transactionType)) {
            newLinkItems = yield getNewLinkItemsWithApplyAmount(
                transactionType,
                linkItems,
                transaction.link_items,
                tooMuchAppliedArr,
                updatedLinkedItemAppliedArr,
                transaction,
            );
        }
        else {
            newLinkItems = yield getNewLinkItemsWithApplyAmount(
                transactionType,
                linkItems,
                transaction.link_items,
                tooMuchAppliedArr,
            );
        }

        // Loop stored link items and see if any of them are no longer applicable
        getInapplicableLinkItems(newLinkItems, transaction.link_items, invalidLinkedItemArr);

        // Update transaction link items to be the same as the linkItems with apply_amount
        transaction.link_items = newLinkItems;

        // Finally, update linkItems in redux. This will trigger linkItemKeys to be updated in respective
        // components (eg DeemedPayments/panelLinks.js), which will then tick/select all applicable link items in UI.
        yield put(changeLinkItems(newLinkItems));
    }
    else if (transactionType === Type.contra) {
        yield readOutstanding(transactionInitCase, transactionType, transaction);
        // ----- Sales -----
        const linkItemsSales = yield select(
            state => state.Transaction.linkItemsWithTableName[Type.sale_payment]);

        const newLinkItemsSales = yield getNewLinkItemsWithApplyAmount(
            transactionType, linkItemsSales, transaction.sales_link_items, tooMuchAppliedArr);

        getInapplicableLinkItems(
            newLinkItemsSales,
            transaction.sales_link_items,
            invalidLinkedItemArr);

        transaction.sales_link_items = newLinkItemsSales;

        yield put(changeLinkItems(newLinkItemsSales, Type.sale_payment));

        // ----- Purchases -----
        const linkItemsPurchases = yield select(
            state => state.Transaction.linkItemsWithTableName[Type.purchase_payment]);

        const newLinkItemsPurchases = yield getNewLinkItemsWithApplyAmount(
            transactionType, linkItemsPurchases, transaction.purchase_link_items, tooMuchAppliedArr);

        getInapplicableLinkItems(
            newLinkItemsPurchases,
            transaction.purchase_link_items,
            invalidLinkedItemArr);

        transaction.purchase_link_items = newLinkItemsPurchases;

        yield put(changeLinkItems(newLinkItemsPurchases, Type.purchase_payment));
    }

    else if ([Type.sale_invoice, Type.purchase_bill].includes(transactionType)) {
        const txnTypeToRead =
                transactionType === Type.sale_invoice ? Type.sale_refund : Type.purchase_refund;

        yield readOutstanding(transactionInitCase, txnTypeToRead, transaction);
        const linkItems = yield select(state => state.Transaction.linkItems);

        const newLinkItems =
            yield getNewLinkItemsWithApplyAmount(
                transactionType,
                linkItems,
                transaction.link_items,
                tooMuchAppliedArr,
                updatedLinkedItemAppliedArr,
                transaction);

        getInapplicableLinkItems(
            newLinkItems, transaction.link_items, invalidLinkedItemArr);

        // remove inapplicable items from transaction.linked_items -- inapplicable and too large applied
        [...invalidLinkedItemArr].forEach(invalidNumber => {
            const obj = transaction.linked_items.find(linked => linked.number === invalidNumber);
            const idx = transaction.linked_items.indexOf(obj);

            if (idx !== -1)
                transaction.linked_items.splice(idx, 1);
        });

        // remove inapplicable items from transaction.linked_items -- where amount <= 0
        [...updatedLinkedItemAppliedArr].forEach(updatedObj => {
            const obj = transaction.linked_items.find(
                linked => (linked.number === updatedObj.number) && updatedObj.toApply <= 0);
            const idx = transaction.linked_items.indexOf(obj);

            if (idx !== -1)
                transaction.linked_items.splice(idx, 1);
        });

        // set the new linked_transaction_ids array
        transaction.linked_transaction_ids = transaction.linked_items.map(item => item.target_transaction_id);

        transaction.link_items = newLinkItems;
        yield put(changeLinkItems(newLinkItems));
    }

    // Finally, prompt modal on any change
    if (invalidLinkedItemArr.length !== 0 || tooMuchAppliedArr.length !== 0 ||
        updatedLinkedItemAppliedArr.length !== 0 || hasNonSubscribedFeatures || hasListItemsChange) {
        const autosaveModalRef = yield select(state => state.Transaction.autosaveModalRef);
        yield showAutosaveConfirmationModalPromise({
            autosaveModalRef,
            invalidLinkedItemArr,
            tooMuchAppliedArr,
            updatedLinkedItemAppliedArr,
            currencyCode: transaction.currency_code,
            transactionType,
            nonBaseUomItems,
            removedBundles,
            subsChanges: {
                hasTagChange, hasCurrencyCodeChange, hasFieldChange, hasLocationChange, hasTaxChange,
            },
            listChanges: {
                invalidAccounts,
                invalidCPs,
                invalidContacts,
                // invalidItemsObj,
                invalidFormItemsObj,
                invalidTermItemsObj,
                invalidDepositItemsObj,
                invalidStockItemsObj,
                invalidBankItemsObj,
                invalidJournalItemsObj,
                invalidTags,
                invalidTerms,
                invalidNumberFormats,
                invalidCustomFields,
            },
            productList,
        }).then(() => {
            // If there are changes made to the transaction, also update in local storage
            const storageItem = {};
            storageItem.data = {...transaction};
            storageItem.last_saved = dayjs().format('DD/MM/YYYY hh:mm:ss a');

            writeAutosaveStorage(transactionType, storageItem);
        });
    }

    return {...transaction};
}
